import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * Snapshot of the currently selected filters across the Insights view.
 * Held by <see cref="InsightsFilterStateService"/> and observable via
 * <c>state$</c>.
 */
export interface InsightsFilterState {
  customerCodes: number[];
  dateFrom: Date;
  dateTo: Date;
  shift: string | null;
  /** Active color theme for the Insights view. Persisted to localStorage and
   *  restored on next entry (#27703). Default 'light' on first open (IV2-06);
   *  an explicit switch is remembered for next session. */
  theme: InsightsTheme;
}

export type InsightsTheme = 'dark' | 'light';

/**
 * Shared filter state for the Insights V2 view. Held at root-singleton scope so
 * card components subscribe to changes and re-fetch when filters change.
 *
 * Default seed: a single customer (resolved later from the user's session
 * context) and a single-day range pointing at today. UI components patch the
 * state via <see cref="setCustomerCodes"/>, <see cref="setDateRange"/>, etc.
 *
 * Eventually the entry from DailyStatus → Insights will pass the current
 * DailyStatus filter context here (customer + date) so the user's selection
 * carries over seamlessly. For now we initialise from defaults; the bridge is
 * added when the DailyStatus banner button gets wired up.
 */
@Injectable({ providedIn: 'root' })
export class InsightsFilterStateService {

  private readonly stateSubject: BehaviorSubject<InsightsFilterState>;

  /** localStorage key for the persisted theme. Same `pdp.*` namespace as the
   *  Insights default-landing flag in app.component. Preserved across the login
   *  localStorage.clear() (see app.component.handleLoginOrSignUpPage). */
  static readonly THEME_KEY = 'pdp.insightsTheme';

  constructor() {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.stateSubject = new BehaviorSubject<InsightsFilterState>({
      customerCodes: [],
      dateFrom: today,
      dateTo: today,
      shift: null,
      // Restore the persisted theme before first paint so there's no dark→light
      // flash when the user previously chose light.
      theme: InsightsFilterStateService.readPersistedTheme()
    });
  }

  /** Reads the persisted theme; defaults to 'light' on first open (IV2-06 / Dan
   *  PPTX s12). An explicit user choice (setTheme/toggleTheme) is persisted and
   *  wins on the next session — so a user who switches to dark stays on dark.
   *  Silent on private-mode. */
  private static readPersistedTheme(): InsightsTheme {
    try {
      const v = localStorage.getItem(InsightsFilterStateService.THEME_KEY);
      if (v === 'light' || v === 'dark') { return v; }
    } catch { /* localStorage blocked — fall through to default */ }
    return 'light';
  }

  /** Observable stream — components subscribe and re-fetch on change. */
  get state$(): Observable<InsightsFilterState> {
    return this.stateSubject.asObservable();
  }

  /** Synchronous snapshot — used when a component needs the current value once (e.g. on init). */
  get snapshot(): InsightsFilterState {
    return this.stateSubject.value;
  }

  get isMultiLocation(): boolean {
    return (this.snapshot.customerCodes?.length ?? 0) > 1;
  }

  get isDateRange(): boolean {
    const s = this.snapshot;
    return !!s.dateFrom && !!s.dateTo && s.dateFrom.getTime() !== s.dateTo.getTime();
  }

  setCustomerCodes(codes: number[]): void {
    this.patch({ customerCodes: codes ?? [] });
  }

  setDateRange(from: Date, to: Date): void {
    this.patch({ dateFrom: from, dateTo: to });
  }

  setShift(shift: string | null): void {
    this.patch({ shift: shift ?? null });
  }

  /** Sets the theme + persists it. Silent on private-mode (in-memory only). */
  setTheme(theme: InsightsTheme): void {
    this.patch({ theme });
    try {
      localStorage.setItem(InsightsFilterStateService.THEME_KEY, theme);
    } catch { /* private-mode browser — preference is session-only */ }
  }

  /** Flips dark ⇄ light and persists. Bound to the filter-bar theme button. */
  toggleTheme(): void {
    this.setTheme(this.snapshot.theme === 'light' ? 'dark' : 'light');
  }

  /** Atomic patch — emits one change event regardless of how many fields are updated. */
  patch(partial: Partial<InsightsFilterState>): void {
    this.stateSubject.next({ ...this.stateSubject.value, ...partial });
  }

  /**
   * Clears all user-scoped filter state back to defaults. Called on logout /
   * login-page entry (app.component.handleLoginOrSignUpPage) so one user's
   * selection can't leak into the next session: this is a root singleton and
   * the SPA does not reload on logout. The theme is deliberately preserved
   * (per-device UI preference, persisted to localStorage; see #27703).
   */
  reset(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.patch({
      customerCodes: [],
      dateFrom: today,
      dateTo: today,
      shift: null
    });
  }
}
